import { useNavigate } from "react-router-dom";
import { AboutVera } from "@/components/AboutVera";
import { HERO_IMAGE } from "@/lib/constants";
import {
  ArrowRight,
  Check,
  Heart,
  Timer,
  FileText,
  FlaskConical,
  Gift,
  Sparkles,
} from "lucide-react";

const BENEFITS = [
  "jouw persoonlijke welzijnsscore",
  "inzicht in stress",
  "mentale helderheid",
  "vermoeidheid",
  "hormonen",
  "beweging",
  "slaap",
  "innerlijke balans",
  "persoonlijke tips",
];

const TRUST = [
  { icon: Timer, label: "Duurt slechts enkele minuten" },
  { icon: Gift, label: "Volledig gratis" },
  { icon: FileText, label: "Persoonlijk rapport" },
  { icon: FlaskConical, label: "Wetenschappelijk opgebouwd" },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBF7F0]">
      {/* Hero */}
      <div className="relative h-[68vh] min-h-[500px] w-full overflow-hidden">
        <img src={HERO_IMAGE} alt="Rust in je hoofd" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3A4A40]/55 via-[#3A4A40]/35 to-[#FBF7F0]" />
        <div className="relative z-10 h-full max-w-3xl mx-auto px-5 flex flex-col justify-center">
          <span className="inline-flex items-center gap-2 self-start bg-white/90 backdrop-blur text-[#5F7D67] text-xs sm:text-sm font-medium px-4 py-2 rounded-full mb-5 animate-fade-up">
            <Heart size={15} className="text-[#C6A24E] fill-[#C6A24E]" />
            Doe de welzijnstest en help ons vrouwen nog beter te helpen
          </span>
          <h1 className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight uppercase animate-fade-up" style={{ animationDelay: "0.1s", textShadow: "0 3px 24px rgba(0,0,0,0.45)" }}>
            Voor vrouwen die hun knop niet meer uit kunnen zetten.
          </h1>
          <button
            data-testid="hero-start-button"
            onClick={() => navigate("/check")}
            className="group mt-7 self-start bg-[#6B8E74] hover:bg-[#56745E] text-white rounded-full px-8 py-4 text-base sm:text-lg font-medium tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(107,142,116,0.4)] inline-flex items-center gap-2 animate-fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            Doe de welzijnstest
            <ArrowRight className="transition-transform group-hover:translate-x-1" size={20} />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 -mt-20 relative z-20 pb-20 space-y-8">
        {/* Empathy */}
        <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-[0_16px_50px_rgba(58,74,64,0.08)] border border-[#EDE7DC] animate-fade-up" style={{ animationDelay: "0.2s" }}>
          <p className="text-lg sm:text-xl text-[#3A4A40] leading-relaxed font-serif italic mb-6">
            Heb jij ook het gevoel dat je altijd "aan" staat?
          </p>
          <p className="text-[#5B6B61] leading-relaxed text-base sm:text-lg mb-5">
            Je bent aan het werken, zorgt voor je gezin, denkt aan duizend dingen tegelijk en tegen dat
            de dag voorbij is draait je hoofd nog altijd op volle toeren.
          </p>
          <div className="space-y-2.5 mb-6">
            {[
              "Je bent sneller geïrriteerd.",
              "Je kunt je minder goed concentreren.",
              "Je vergeet vaker kleine dingen.",
              "Je voelt dat je hoofd nooit echt tot rust komt.",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C88A6A] mt-2.5 shrink-0" />
                <span className="text-[#3A4A40] text-base sm:text-lg">{t}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-[#EDE7DC] my-6" />
          <p className="text-[#5B6B61] leading-relaxed text-base sm:text-lg mb-4">
            Bij <span className="text-[#3A4A40] font-medium">Gezond met Vera</span> ontwikkelen we momenteel{" "}
            <span className="text-[#5F7D67] font-medium">NUNNU Calm &amp; Clarity</span> — een nieuw product
            speciaal voor vrouwen die weer meer rust in hun hoofd willen ervaren en zich opnieuw helder,
            energiek en zichzelf willen voelen.
          </p>
          <p className="text-[#5B6B61] leading-relaxed text-base sm:text-lg mb-4">
            Maar voordat we het lanceren willen we zeker zijn dat we niets over het hoofd zien van wat
            vrouwen écht nodig hebben. Daarom vragen we jouw hulp.
          </p>
          <p className="text-[#5B6B61] leading-relaxed text-base sm:text-lg">
            Door deze welzijnsmonitor in te vullen help je ons een product te ontwikkelen dat nog beter
            aansluit bij de uitdagingen waar vrouwen vandaag mee worstelen — zodat we samen bouwen aan
            meer rust in je hoofd, helderder denken en je opnieuw jezelf voelen.
          </p>
        </div>

        {/* Benefits */}
        <div className="bg-[#F1EEE5] rounded-[28px] p-6 sm:p-10 border border-[#E5DFD2] animate-fade-up">
          <p className="inline-flex items-center gap-2.5 text-2xl sm:text-3xl text-[#C88A6A] font-bold mb-6 leading-snug">
            <Sparkles size={26} className="shrink-0" /> Als bedankje ontvang je onmiddellijk
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
            {BENEFITS.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <span className="bg-[#6B8E74] text-white rounded-full p-1 shrink-0">
                  <Check size={13} strokeWidth={3} />
                </span>
                <span className="text-[#3A4A40] capitalize text-base sm:text-lg font-medium">{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-up">
          <button
            data-testid="start-survey-button"
            onClick={() => navigate("/check")}
            className="group w-full sm:w-auto bg-[#6B8E74] hover:bg-[#56745E] text-white rounded-full px-10 py-5 text-lg font-medium tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(107,142,116,0.35)] inline-flex items-center justify-center gap-2"
          >
            START MIJN GRATIS WELZIJNSMONITOR
            <ArrowRight className="transition-transform group-hover:translate-x-1" size={20} />
          </button>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6">
            {TRUST.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5 text-sm text-[#5F7D67]">
                <Icon size={15} className="text-[#C6A24E]" /> {label}
              </span>
            ))}
          </div>
        </div>

        {/* About Vera */}
        <div className="animate-fade-up">
          <AboutVera />
        </div>

        <footer className="text-center text-sm text-[#8FA38C]">
          <a href="/privacy" data-testid="privacy-link" className="underline hover:text-[#5B6B61]">
            Privacyverklaring (GDPR)
          </a>
          <span className="mx-2">•</span>
          <a href="/admin/login" data-testid="admin-login-link" className="hover:text-[#5B6B61]">
            Beheer
          </a>
        </footer>
      </div>
    </div>
  );
}
